import { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * UN WIDGET FLOTANTE, EN EL CENTRO.
 *
 * Esteban, 20-sep: *"me abra un widget de las cosas más importantes que sí
 * hay... debería desplegarse un widget en el centro, centro de la ventana,
 * flotante, no en un lateral, me está desviando la vista"*.
 *
 * ═══ POR QUÉ EL CENTRO Y NO UN PANEL LATERAL ═══
 *
 * La hoja del asunto abre a la derecha porque es un DETALLE: se mira mientras
 * la lista sigue ahí. Esto es otra cosa — es una respuesta a "¿qué es lo
 * urgente?", y la respuesta tiene que caer donde ya estaban los ojos. Un panel
 * lateral obliga a mover la vista de un lado al otro de una pantalla de 27
 * pulgadas, y eso cansa antes de la tercera vez.
 *
 * En móvil sigue siendo una hoja desde abajo: en 390 px no hay "centro" que
 * valga, y una caja flotante con márgenes deja menos espacio útil.
 */

interface Props {
  abierto: boolean;
  titulo: string;
  /** Una línea bajo el título. Opcional. */
  bajada?: string;
  onCerrar: () => void;
  children: React.ReactNode;
}

export function Widget({ abierto, titulo, bajada, onCerrar, children }: Props) {
  // Escape cierra. Es lo que todo el mundo intenta primero.
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [abierto, onCerrar]);

  if (!abierto) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[3px]"
        onClick={onCerrar}
        aria-hidden
      />
      <div
        role="dialog"
        aria-label={titulo}
        className={[
          'fixed z-50 flex flex-col bg-white shadow-2xl dark:bg-slate-900',
          // Móvil: hoja desde abajo.
          'inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl',
          // Escritorio: CENTRADO de verdad, con `translate` sobre el 50%.
          'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:max-h-[78vh] sm:w-[560px]',
          'sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl',
        ].join(' ')}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">{titulo}</h2>
            {bajada && (
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{bajada}</p>
            )}
          </div>
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-1">{children}</div>
      </div>
    </>
  );
}
